import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import BetaTicker from '@/components/BetaTicker';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import LoginModal from '@/components/auth/login-modal';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { useAuth } from '@/auth/AuthProvider';
import { Play, GalleryThumbnails, Check, ArrowRight, ShieldCheck } from 'lucide-react';
// Using assets from public/landing-assets (copied from landing-page-package during build)
const oceanImage = '/landing-assets/assets/image-from-rawpixel-id-9758859-jpeg.jpg';
const seascapeImage = '/landing-assets/assets/seascape.jpg';
const calmSeaImage = '/landing-assets/assets/The_Calm_Sea_MET_DT1973.jpg';
import { LumiChatWindow, ScientificPlotWindow, ScientificBoxPlotMultiWindow, PlotTypeGalleryWindow, PlotEditorWindow } from '@/components/DemoWindowPlot';
import { MultiPanelWindow } from '@/components/DemoWindowMultiPanel';
import { LumiLogoRound } from '@/components/icons/lumi-logo-round';
import { getBlogUrl, getHelpUrl } from '@/lib/urls';
import ExamplePlotsCarousel from '@/components/ExamplePlotsCarousel';

// Social media icons from public/landing-assets
const xIcon = '/landing-assets/social_media_icons/x.svg';
const linkedinIcon = '/landing-assets/social_media_icons/linkedin.svg';
const blueskyIcon = '/landing-assets/social_media_icons/bluesky.svg';

// Text window component for displaying caption, results, and next steps
function TextContentWindow({
  width = 680,
  height = 560,
  centered = true,
}: {
  width?: number;
  height?: number;
  centered?: boolean;
}) {
  return (
    <motion.div
      aria-hidden="true"
      initial={{ opacity: 0, scale: 0.96, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={[
        "group relative select-none overflow-hidden rounded-xl",
        "shadow-[0_28px_70px_rgba(0,0,0,0.14),_0_14px_32px_rgba(0,0,0,0.10),_0_0_0_1px_rgba(0,0,0,0.08)]",
        "border border-black/10 bg-white/95 backdrop-blur",
        centered ? "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" : "relative",
      ].join(" ")}
      style={{ width, height, zIndex: 10 }}
    >
      <div className="relative flex h-7 items-center justify-between border-b border-black/10 px-2 bg-neutral-50/90">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
        </div>
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-neutral-700">
          Lumiplot
        </div>
        <div className="-mr-1 flex items-center">
          <button
            type="button"
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
            disabled
          >
            Open app
          </button>
        </div>
      </div>

      <div className="relative h-full w-full overflow-auto p-6" style={{ height: 'calc(100% - 28px)' }}>
        <div className="max-w-2xl space-y-6 text-left">
          <div>
            <h4 className="text-lg font-semibold text-foreground mb-2">Figure caption</h4>
            <p className="text-sm text-foreground leading-relaxed">
              Grouped vertical bar chart showing mean value by group (A-D) and treatment (Control, Treatment) with 95% confidence interval error bars; x-axis is Group, y-axis is Mean Value.
            </p>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-foreground mb-2">Results</h4>
            <div className="text-sm text-foreground leading-relaxed space-y-2">
              <p>
                <strong>Significant group effect on mean value:</strong> Two-way ANOVA shows a significant effect of group on mean value (F<sub>3,152</sub>=5.57, p&lt;0.001), with a small effect size (eta²=0.026).
              </p>
              <p>
                <strong>Higher mean values under Treatment:</strong> Mean values are consistently higher for Treatment than Control across all groups, with non-overlapping 95% confidence intervals in most cases.
              </p>
            </div>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-foreground mb-2">Next steps</h4>
            <p className="text-sm text-foreground leading-relaxed">
              Provide pairwise comparisons with adjusted p-values to identify which groups differ significantly.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Landing() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly'|'yearly'>('monthly');
  const [activeTab, setActiveTab] = useState<string>('enterprise-agents');
  const { go } = useNav();
  const { user } = useAuth();
  const blogUrl = getBlogUrl();

  // Update document title and meta description for SEO
  useEffect(() => {
    document.title = 'Lumiplot - From Data to Publication in 60 Seconds | AI-Powered Scientific Visualization';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Transform your research data into publication-ready figures with natural language. Get both instant visual editing and reproducible Python code. Built by researchers, for researchers.');
    
    // Update Open Graph tags
    const updateMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    updateMetaTag('og:title', 'Lumiplot - From Data to Publication in 60 Seconds');
    updateMetaTag('og:description', 'Transform your research data into publication-ready figures with natural language. Get both instant visual editing and reproducible Python code.');
    
    return () => {
      // Optionally reset title on unmount
      document.title = 'Lumiplot';
    };
  }, []);

  // Scroll to top of content container on mount to show enterprise-agents section
  useEffect(() => {
    const contentContainer = document.getElementById('section-standard-content');
    if (contentContainer) {
      contentContainer.scrollTop = 0;
    }
  }, []);

  // IntersectionObserver to track visible sections and update active tab
  useEffect(() => {
    const contentContainer = document.getElementById('section-standard-content');
    if (!contentContainer) return;

    const observerOptions = {
      root: contentContainer,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          if (id) {
            setActiveTab(id);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const sections = [
      'enterprise-agents',
      'ai-powered-search',
      'deep-research',
      'complete-components',
    ];

    // Use setTimeout to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      sections.forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          observer.observe(element);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      sections.forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-background pt-10" data-testid="landing-page" style={{ overscrollBehavior: 'contain' }}>
      <BetaTicker />
      {/* Header */}
      <header className="sticky top-8 z-40 border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="logo">
            <LumiLogoRound size={32} />
          </div>
          
          <nav className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Features
            </a>
            <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Pricing
            </a>
            <a
              href={blogUrl}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Blog
            </a>
            <a
              href={getHelpUrl()}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Docs
            </a>
          </nav>
          
          <Button 
            onClick={() => setShowLoginModal(true)}
            data-testid="button-login"
          >
            Sign In
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 pt-20 pb-8 text-left">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-prose p-6">
          <Badge variant="secondary" className="mb-4">
            AI-Powered Scientific Visualization
          </Badge>
          
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
            <span className="text-primary">From Data to</span> Publication-Ready Figures
          </h1>
          
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl">
            Turn raw datasets into statistically rigorous, journal-ready visuals with simple natural-language prompts. No coding required. Built by researchers, for researchers.
          </p>
          
          <div className="flex gap-4 justify-start">
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90"
              onClick={() => (user ? go(routes.plotEditor.new()) : setShowLoginModal(true))}
              data-testid="button-try-demo"
            >
              <Play className="mr-2 h-4 w-4" />
              Start plotting
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              data-testid="button-view-examples"
              onClick={() => { window.location.href = 'https://blog.lumiplot.ai/help/examples/bio/'; }}
            >
              <GalleryThumbnails className="mr-2 h-4 w-4" />
              View Examples
            </Button>
            {/* Removed: Read the Docs button */}
          </div>
          </div>
        </div>
      </section>

      {/* Example Plots Carousel Section */}
      <section className="pt-4 pb-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-left mt-12 mb-0 p-6">
              <h2 className="text-3xl font-bold mb-4">See Lumiplot in Action</h2>
              <p className="text-lg text-muted-foreground max-w-2xl">
                Explore example figures from real datasets and see how researchers use Lumiplot for publication-ready papers and reports.
              </p>
            </div>
            <ExamplePlotsCarousel />
          </div>
        </div>
      </section>

      {/* Inserted Cursor-style cards for Lumiplot */}
      {/* Lumi Section */}
      <section id="features" className="py-16 bg-background" data-testid="lumichat-section" style={{ overscrollBehavior: 'contain' }}>
        <div className="max-w-5xl mx-auto px-4">
          <a 
            href="#features" 
            className="block cursor-card bg-white overflow-hidden rounded border border-neutral-200 hover:border-neutral-300 transition-colors relative"
            style={{ overscrollBehavior: 'contain' }}
          >
            <div 
              className="absolute inset-0 opacity-[0.45]"
              style={{
                backgroundImage: `url(${seascapeImage})`,
                backgroundSize: '120% 120%',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            <div className="grid lg:grid-cols-12 gap-0 min-h-[680px] relative" style={{ overscrollBehavior: 'contain' }}>
              <div className="col-span-12 lg:col-span-4 flex items-center p-8 lg:p-12 bg-white/95 rounded">
                <div className="max-w-prose">
                  <h3 className="text-2xl md:text-3xl font-bold cursor-text-primary leading-tight mb-4">
                    AI-powered chat assistant
                  </h3>
                  <p className="text-base cursor-text-secondary leading-relaxed mb-6">
                    Simply describe what you want to visualize in natural language. Lumi understands your intent and proposes multiple visualization approaches tailored to your data.
                  </p>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      user ? go(routes.plotEditor.new()) : setShowLoginModal(true);
                    }}
                    className="inline-flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-70 cursor-pointer bg-transparent border-0 p-0" 
                    style={{color: '#ffd500'}}
                  >
                    Try Lumi <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-8 relative p-8 lg:p-12 overflow-visible" style={{ overscrollBehavior: 'contain' }}>
                <LumiChatWindow centered={true} />
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Plot Editor GUI Section */}
      <section className="py-16 bg-background" data-testid="plot-editor-section" style={{ overscrollBehavior: 'contain' }}>
        <div className="max-w-5xl mx-auto px-4">
          <a 
            href="#features" 
            className="block cursor-card bg-white overflow-hidden rounded border border-neutral-200 hover:border-neutral-300 transition-colors relative"
            style={{ overscrollBehavior: 'contain' }}
          >
            <div 
              className="absolute inset-0 opacity-[0.45]"
              style={{
                backgroundImage: `url(${calmSeaImage})`,
                backgroundSize: '120% 120%',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            <div className="grid lg:grid-cols-12 gap-0 min-h-[600px] relative" style={{ overscrollBehavior: 'contain' }}>
              {/* Painting area - left 8 columns */}
              <div className="col-span-12 lg:col-span-8 relative min-h-[600px]" style={{ overscrollBehavior: 'contain' }}>
                {/* Plot Editor Window centered within painting area */}
                <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 z-10">
                  <PlotEditorWindow centered={false} width={604.8} height={411.3} />
                </div>
              </div>
              {/* Right side text content */}
              <div className="col-span-12 lg:col-span-4 flex items-center p-8 lg:p-12 bg-white/95 relative z-20 rounded">
                <div className="max-w-prose">
                  <h3 className="text-2xl md:text-3xl font-bold cursor-text-primary leading-tight mb-4">
                    Visual plot editor
                  </h3>
                  <p className="text-base cursor-text-secondary leading-relaxed mb-6">
                    Fine-tune every detail of your visualization with our intuitive GUI editor. Adjust axes, customize series, modify colors, and add annotations—all without writing code. See changes instantly as you edit.
                  </p>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      user ? go(routes.plotEditor.new()) : setShowLoginModal(true);
                    }}
                    className="inline-flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-70 cursor-pointer bg-transparent border-0 p-0" 
                    style={{color: '#ffd500'}}
                  >
                    Try the editor <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Multipanel GUI Section */}
      <section
        className="py-16 bg-background"
        data-testid="multipanel-section"
        style={{ overscrollBehavior: 'contain' }}
      >
        <div className="max-w-5xl mx-auto px-4">
          <a
            href="#features"
            className="block cursor-card bg-white overflow-hidden rounded border border-neutral-200 hover:border-neutral-300 transition-colors relative"
          >
            <div 
              className="absolute inset-0 opacity-[0.45]"
              style={{
                backgroundImage: `url(${oceanImage})`,
                backgroundSize: '120% 120%',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            <div className="grid lg:grid-cols-12 gap-0 min-h-[600px] relative">
              {/* Left: text copy */}
              <div className="col-span-12 lg:col-span-4 flex items-center p-8 lg:p-12 bg-white/95 relative z-20 rounded">
                <div className="max-w-prose">
                  <h3 className="text-2xl md:text-3xl font-bold leading-tight mb-4">
                    Build multipanel figures
                  </h3>
                  <p className="text-base leading-relaxed mb-6">
                    Drag plots from your library into a shared canvas to assemble publication-ready multipanel figures. Reorder panels, swap plots, and keep labels synchronized across the whole figure.
                  </p>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      user ? go(routes.plotEditor.new()) : setShowLoginModal(true);
                    }}
                    className="inline-flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-70 cursor-pointer bg-transparent border-0 p-0" 
                    style={{ color: '#ffd500' }}
                  >
                    Try multipanel editing <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {/* Right: animation window */}
              <div className="col-span-12 lg:col-span-8 relative min-h-[600px]">
                <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 z-10">
                  <MultiPanelWindow centered={false} width={604.8} height={411.3} />
                </div>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Features Navigation Section */}
      <section className="py-16 bg-background">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-left mb-16 p-6">
            <h2 className="text-3xl font-bold mb-4">Why Researchers Choose Lumiplot</h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Stop wasting hours on figure formatting. Focus on your research while Lumi handles the visualization.
            </p>
          </div>
          <div className="flex scroll-smooth">
            {/* Sidebar Navigation */}
            <div className="flex shrink-0 flex-col divide-y divide-foreground border border-foreground sm:w-64 md:h-[475px] rounded overflow-hidden">
              <button
                onClick={() => {
                  setActiveTab('enterprise-agents');
                  document.getElementById('enterprise-agents')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={`group relative flex min-h-[49px] items-center pl-5 transition-[color] rounded-t ${
                  activeTab === 'enterprise-agents' 
                    ? 'bg-foreground text-background' 
                    : 'bg-background text-foreground'
                }`}
              >
                <div className="max-w-[199px] text-left text-sm">
                  <p>Publication-grade figures</p>
                </div>
                <div className={`title-icon-standard absolute right-5 top-1/2 size-5 shrink-0 -translate-y-1/2 text-primary transition-[color] svg-container ${
                  activeTab === 'enterprise-agents' ? 'opacity-100' : 'opacity-0'
                }`} style={{ width: '20px', height: '20px' }}>
                  <svg viewBox="0 0 21 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 9.5L16 9.5M16 9.5L12 5.5M16 9.5L12 13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </button>
              <button
                onClick={() => {
                  setActiveTab('ai-powered-search');
                  document.getElementById('ai-powered-search')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={`group relative flex min-h-[49px] items-center pl-5 transition-[color] ${
                  activeTab === 'ai-powered-search' 
                    ? 'bg-foreground text-background' 
                    : 'bg-background text-foreground'
                }`}
              >
                <div className="max-w-[199px] text-left text-sm">
                  <p>Automated statistical tests</p>
                </div>
                <div className={`title-icon-standard absolute right-5 top-1/2 size-5 shrink-0 -translate-y-1/2 text-primary transition-[color] svg-container ${
                  activeTab === 'ai-powered-search' ? 'opacity-100' : 'opacity-0'
                }`} style={{ width: '20px', height: '20px' }}>
                  <svg viewBox="0 0 21 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 9.5L16 9.5M16 9.5L12 5.5M16 9.5L12 13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </button>
              <button
                onClick={() => {
                  setActiveTab('deep-research');
                  document.getElementById('deep-research')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={`group relative flex min-h-[49px] items-center pl-5 transition-[color] ${
                  activeTab === 'deep-research' 
                    ? 'bg-foreground text-background' 
                    : 'bg-background text-foreground'
                }`}
              >
                <div className="max-w-[199px] text-left text-sm">
                  <p>Every chart type you need</p>
                </div>
                <div className={`title-icon-standard absolute right-5 top-1/2 size-5 shrink-0 -translate-y-1/2 text-primary transition-[color] svg-container ${
                  activeTab === 'deep-research' ? 'opacity-100' : 'opacity-0'
                }`} style={{ width: '20px', height: '20px' }}>
                  <svg viewBox="0 0 21 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 9.5L16 9.5M16 9.5L12 5.5M16 9.5L12 13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </button>
              <button
                onClick={() => {
                  setActiveTab('complete-components');
                  document.getElementById('complete-components')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={`group relative flex min-h-[49px] items-center pl-5 transition-[color] rounded-b ${
                  activeTab === 'complete-components' 
                    ? 'bg-foreground text-background' 
                    : 'bg-background text-foreground'
                }`}
              >
                <div className="max-w-[199px] text-left text-sm">
                  <p>Every component you need</p>
                </div>
                <div className={`title-icon-standard absolute right-5 top-1/2 size-5 shrink-0 -translate-y-1/2 text-primary transition-[color] svg-container ${
                  activeTab === 'complete-components' ? 'opacity-100' : 'opacity-0'
                }`} style={{ width: '20px', height: '20px' }}>
                  <svg viewBox="0 0 21 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 9.5L16 9.5M16 9.5L12 5.5M16 9.5L12 13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </button>
            </div>

            {/* Content Sections */}
            <div id="section-standard-content" className="no-scrollbar flex flex-1 flex-col gap-y-3xl overflow-y-scroll border-b-[0.5px] border-[#f5edd3] md:h-[475px] rounded overflow-hidden">
              <div data-id="13" id="enterprise-agents" className="content-standard-tab-observed grid grid-cols-10 md:h-[475px]">
                <div className="relative col-span-3 shrink-0 bg-[#f5edd3] md:h-[475px]" style={{ backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px)', backgroundSize: '51px 51px' }}>
                  <div className="flex size-full flex-1 flex-col items-start justify-center border-b border-r border-secondary px-8 py-12 sm:px-12 sm:py-16 bg-white md:h-[475px] rounded-tl rounded-l">
                    <div className="max-w-[280px] space-y-5">
                      <h3 className="text-2xl leading-[1.3] font-bold text-foreground tracking-tight">Publication-ready scientific plots</h3>
                      <p className="text-sm leading-[1.5] text-muted-foreground font-normal">Generate publication-quality figures with regression analysis, confidence intervals, and outlier detection - all from natural language commands.</p>
                    </div>
                  </div>
                </div>
                <div className="relative size-full col-span-7 shrink-0 bg-transparent md:h-[475px] flex items-center justify-center rounded-tr rounded-r" style={{ backgroundColor: '#ffd500' }}>
                  <div className="flex items-center justify-center" style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}>
                    <div style={{ width: '546px', height: '404px', transform: 'scale(0.85)', transformOrigin: 'center center' }}>
                      <ScientificPlotWindow />
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="14" id="ai-powered-search" className="content-standard-tab-observed grid grid-cols-10 md:h-[475px]">
                <div className="relative col-span-3 shrink-0 bg-[#f5edd3] md:h-[475px]" style={{ backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px)', backgroundSize: '51px 51px' }}>
                  <div className="flex size-full flex-1 flex-col items-start justify-center border-b border-r border-secondary px-8 py-12 sm:px-12 sm:py-16 bg-white md:h-[475px] rounded-l">
                    <div className="max-w-[280px] space-y-5">
                      <h3 className="text-2xl leading-[1.3] font-bold text-foreground tracking-tight">Statistical overlays ready to publish</h3>
                      <p className="text-sm leading-[1.5] text-muted-foreground font-normal">Generate publication-quality plots with automated statistical testing. Mann-Whitney U, Wilcoxon signed-rank, and significance markers - all computed and beautifully overlaid.</p>
                    </div>
                  </div>
                </div>
                <div className="relative size-full col-span-7 shrink-0 bg-transparent md:h-[475px] flex items-center justify-center rounded-r" style={{ backgroundColor: '#ffd500' }}>
                  <div className="flex items-center justify-center" style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}>
                    <div style={{ width: '546px', height: '404px', transform: 'scale(0.85)', transformOrigin: 'center center' }}>
                      <ScientificBoxPlotMultiWindow />
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="28" id="deep-research" className="content-standard-tab-observed grid grid-cols-10 md:h-[475px]">
                <div className="relative col-span-3 shrink-0 bg-[#f5edd3] md:h-[475px]" style={{ backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px)', backgroundSize: '51px 51px' }}>
                  <div className="flex size-full flex-1 flex-col items-start justify-center border-b border-r border-secondary px-8 py-12 sm:px-12 sm:py-16 bg-white md:h-[475px] rounded-l">
                    <div className="max-w-[280px] space-y-5">
                      <h3 className="text-2xl leading-[1.3] font-bold text-foreground tracking-tight">Every chart type you need</h3>
                      <p className="text-sm leading-[1.5] text-muted-foreground font-normal">From spine charts to sankey diagrams, we support all the visualization types you need for data analysis. Choose from 40 professional chart types inspired by the Financial Times visual vocabulary.</p>
                    </div>
                  </div>
                </div>
                <div className="relative size-full col-span-7 shrink-0 bg-transparent md:h-[475px] flex items-start justify-center pt-4 rounded-r" style={{ backgroundColor: '#ffd500' }}>
                  <div className="flex items-start justify-center" style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}>
                    <div style={{ width: '546px', height: '404px', transform: 'scale(0.7)', transformOrigin: 'center top' }}>
                      <PlotTypeGalleryWindow />
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="29" id="complete-components" className="content-standard-tab-observed grid grid-cols-10 md:h-[475px]">
                <div className="relative col-span-3 shrink-0 bg-[#f5edd3] md:h-[475px]" style={{ backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px)', backgroundSize: '51px 51px' }}>
                  <div className="flex size-full flex-1 flex-col items-start justify-center border-b border-r border-secondary px-8 py-12 sm:px-12 sm:py-16 bg-white md:h-[475px] rounded-bl rounded-l">
                    <div className="max-w-[280px] space-y-5">
                      <h3 className="text-2xl leading-[1.3] font-bold text-foreground tracking-tight">Turn any plot into a finished figure.</h3>
                      <p className="text-sm leading-[1.5] text-muted-foreground font-normal">Lumiplot writes the caption, highlights key stats, and suggests next steps.</p>
                    </div>
                  </div>
                </div>
                <div className="relative size-full col-span-7 shrink-0 bg-transparent md:h-[475px] flex items-center justify-center rounded-br rounded-r" style={{ backgroundColor: '#ffd500' }}>
                  <div className="flex items-center justify-center" style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}>
                    <div style={{ width: '546px', height: '404px', transform: 'scale(0.85)', transformOrigin: 'center center' }}>
                      <TextContentWindow centered={false} width={546} height={404} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security and Data Privacy Section */}
      <section className="py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck className="w-6 h-6 text-foreground" />
              <span className="text-base text-muted-foreground font-medium">Security and Data Privacy</span>
            </div>
            
            <h4 className="text-2xl md:text-3xl font-bold text-foreground mb-12 leading-tight">
              Your research is valuable and private - and we protect it so it stays that way.
            </h4>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-muted-foreground">
                <p className="mb-4">
                  <strong className="text-foreground font-semibold">Your data stays yours.</strong> You retain all rights to everything you upload: datasets, prompts, and content. You also own all outputs generated by Lumiplot. These are not shared outside your private workspace, which you control, and can be deleted by you at any time.
                </p>
              </div>
              
              <div className="text-muted-foreground">
                <p className="mb-4">
                  <strong className="text-foreground font-semibold">Secure by default.</strong> Lumiplot is designed from the ground up with modern security best practices, with the privacy of your data in mind. Information you upload is only viewable by users in your workspace, which you control, and can be deleted by you at any time.
                </p>
              </div>
              
              <div className="text-muted-foreground">
                <p className="mb-4">
                  <strong className="text-foreground font-semibold">We don't train on your content.</strong> We do not use your uploaded content or generated outputs to train third-party foundation models. We do not send your data directly to LLM. Your data stays in your private workspace and analysis runs within your workspace. What you share stays private.
                </p>
                <p className="text-sm text-muted-foreground mt-4">
                  Learn more in our <a href={routes.legal.terms()} className="text-foreground hover:underline transition-colors">Terms</a>, <a href={routes.legal.privacy()} className="text-foreground hover:underline transition-colors">Privacy</a>, and <a href={routes.legal.data()} className="text-foreground hover:underline transition-colors">Data Use</a> pages.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-8 bg-background">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-left mb-16">
            <h2 className="text-3xl font-bold mb-4">Simple, Scientific Pricing</h2>
            <p className="text-lg text-muted-foreground">
              Start free, upgrade when you need more
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
          {/* Free Plan */}
          <Card data-testid="pricing-free">
            <CardHeader>
              <CardTitle className="text-2xl">Free</CardTitle>
              <CardDescription>No credit card required to get started</CardDescription>
              <div className="text-3xl font-bold">$0<span className="text-sm font-normal">/month</span></div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>300<sup className="text-xs leading-none -ml-0.5">*</sup> credits per month (≈100 plots)</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  1 project
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Unlimited chart editing
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Export PNG, SVG, Python code
                </li>
              </ul>
              <Button 
                className="w-full"
                onClick={() => setShowLoginModal(true)}
                data-testid="button-start-free"
              >
                Start Free
              </Button>
            </CardContent>
          </Card>

          {/* Plus Plan (Most Popular) */}
          <Card className="border-primary relative" data-testid="pricing-plus">
            <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-primary">
              Most Popular
            </Badge>
            <CardHeader>
              <CardTitle className="text-2xl">Plus</CardTitle>
              <CardDescription>Built for professionals</CardDescription>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold">{billingCycle === 'yearly' ? '$129' : '$12.9'}<span className="text-sm font-normal">{billingCycle === 'yearly' ? '/year' : '/month'}</span></div>
                <button 
                  type="button" 
                  role="switch" 
                  aria-checked={billingCycle === 'yearly'} 
                  data-state={billingCycle === 'yearly' ? 'checked' : 'unchecked'} 
                  value="on" 
                  className="peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=unchecked]:bg-input data-[state=checked]:bg-[#ffd500]"
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                >
                  <span 
                    data-state={billingCycle === 'yearly' ? 'checked' : 'unchecked'} 
                    className="pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
                  ></span>
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Unlimited credits
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Unlimited saved plots
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Early access to new features
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Priority support
                </li>
              </ul>
              <Button 
                className="w-full bg-primary"
                onClick={async () => {
                  try {
                    const res = await fetch('/api/config');
                    const cfg = await res.json();
                    const priceId = billingCycle === 'yearly' ? cfg.pricing?.plusYearly : cfg.pricing?.plusMonthly;
                    if (!priceId) { setShowLoginModal(true); return; }
                    const resp = await fetch('/api/billing/checkout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ priceId }) });
                    const out = await resp.json();
                    if (out?.url) window.location.href = out.url; else setShowLoginModal(true);
                  } catch {
                    setShowLoginModal(true);
                  }
                }}
                data-testid="button-upgrade-plus"
              >
                Upgrade to Plus
              </Button>
            </CardContent>
          </Card>

          {/* Team Plan */}
          <Card className="relative" data-testid="pricing-team">
            <CardHeader>
              <CardTitle className="text-2xl">Team</CardTitle>
              <CardDescription>Everything in Plus, for teams</CardDescription>
              <div className="text-3xl font-bold">Custom<span className="text-sm font-normal">/org</span></div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Unlimited collaborators
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Team workspaces, shared datasets
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Role-based access
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Expedited queue
                </li>
              </ul>
              <Button 
                className="w-full bg-primary"
                onClick={() => (window.location.href = 'mailto:hi@lumiplot.ai?subject=Lumiplot%20Team%20Plan')}
                data-testid="button-contact-team"
              >
                Contact us
              </Button>
            </CardContent>
          </Card>
          </div>
          <p className="text-xs text-muted-foreground mt-6 text-left">
            *Founding offer: Sign up by May 30, 2026 (11:59 PM PT) to lock in 300 credits/month on Free for 12 months. After that, Free includes fewer monthly credits.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <LumiLogoRound size={24} />
                <span className="font-medium">Lumiplot</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <a href={routes.legal.terms()} className="text-muted-foreground hover:text-foreground transition-colors">
                  Terms
                </a>
                <a href={routes.legal.privacy()} className="text-muted-foreground hover:text-foreground transition-colors">
                  Privacy
                </a>
                <a href={routes.legal.data()} className="text-muted-foreground hover:text-foreground transition-colors">
                  Data Use
                </a>
              </div>
              <div className="text-sm text-muted-foreground">© {new Date().getFullYear()} Lumiplot. All rights reserved.</div>
            </div>
            <div className="inline-flex items-center gap-3">
              {/* Replace href="#" with real profile links when ready */}
              <a 
                href="https://x.com/lumiplot" 
                target="_blank" 
                rel="noopener noreferrer" 
                aria-label="X" 
                className="opacity-80 hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.preventDefault();
                  window.open('https://x.com/lumiplot', '_blank', 'noopener,noreferrer');
                }}
              >
                  <img src={xIcon} alt="X" className="h-4 w-4" />
                </a>
                <a 
                  href="https://www.linkedin.com/company/lumiplot/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="LinkedIn" 
                  className="opacity-80 hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('https://www.linkedin.com/company/lumiplot/', '_blank', 'noopener,noreferrer');
                  }}
                >
                  <img src={linkedinIcon} alt="LinkedIn" className="h-4 w-4" />
                </a>
                <a 
                  href="https://bsky.app/profile/lumiplot.bsky.social" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="Bluesky" 
                  className="opacity-80 hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('https://bsky.app/profile/lumiplot.bsky.social', '_blank', 'noopener,noreferrer');
                  }}
                >
                  <img src={blueskyIcon} alt="Bluesky" className="h-4 w-4" />
                </a>
            </div>
          </div>
        </div>
      </footer>

      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
    </div>
  );
}




